import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSocket } from '../../contexts/SocketContext';
import {
  MenuIcon,
  XIcon,
  LogoutIcon,
  ChatIcon,
  SparklesIcon
} from '@heroicons/react/outline';
import { motion, AnimatePresence } from 'framer-motion';
import messageService from '../../services/messageService';
import UserAvatar from './UserAvatar';
import NotificationCenter from './NotificationCenter';

// Check if full app is enabled - outside component to avoid recreation
const ENABLE_FULL_APP = process.env.REACT_APP_ENABLE_FULL_APP === 'true';

const Header = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const { socket } = useSocket();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [unreadMessageCount, setUnreadMessageCount] = useState(0);

  const fetchUnreadMessageCount = useCallback(async () => {
    try {
      const response = await messageService.getUnreadCount();
      setUnreadMessageCount(response.unreadCount);
    } catch (error) {
      console.error('Failed to fetch unread message count:', error);
    }
  }, []);

  // Fetch unread message count
  useEffect(() => {
    if (isAuthenticated) {
      fetchUnreadMessageCount();
    }
  }, [isAuthenticated, fetchUnreadMessageCount]);

  // Listen for new messages
  useEffect(() => {
    if (!socket || !isAuthenticated) return;

    const handleNewMessage = () => {
      fetchUnreadMessageCount();
    };

    const handleMessageRead = () => {
      fetchUnreadMessageCount();
    };

    const handleMessagesMarkedRead = (data) => {
      // Directly update the count without fetching from server
      setUnreadMessageCount(prev => Math.max(0, prev - data.count));
    };

    socket.on('new_message', handleNewMessage);
    socket.on('message_read', handleMessageRead);
    socket.on('messages_marked_read', handleMessagesMarkedRead);

    return () => {
      socket.off('new_message', handleNewMessage);
      socket.off('message_read', handleMessageRead);
      socket.off('messages_marked_read', handleMessagesMarkedRead);
    };
  }, [socket, isAuthenticated, fetchUnreadMessageCount]);

  // Memoize nav links to prevent recreation on every render
  const navLinks = useMemo(() => {
    if (!isAuthenticated) return [];

    switch (user?.role) {
      case 'customer':
        return [
          { name: 'Dashboard', path: '/customer/dashboard' },
          { name: 'Book Session', path: '/customer/book' },
          { name: 'My Sessions', path: '/customer/sessions' },
          { name: 'Profile', path: '/customer/profile' }
        ];
      case 'bodyguard':
        // In soft-launch, only the dashboard (application status) is functional
        if (!ENABLE_FULL_APP) {
          return [{ name: 'Application Status', path: '/bodyguard/dashboard' }];
        }
        return [
          { name: 'Dashboard', path: '/bodyguard/dashboard' },
          { name: 'Requests', path: '/bodyguard/requests' },
          { name: 'Manage Sessions', path: '/bodyguard/sessions' },
          { name: 'Earnings', path: '/bodyguard/earnings' },
          { name: 'Profile', path: '/bodyguard/profile' }
        ];
      case 'admin':
        return [
          { name: 'Dashboard', path: '/admin/dashboard' },
          { name: 'Applications', path: '/admin/applications' },
          { name: 'Sessions', path: '/admin/sessions' },
          { name: 'Users', path: '/admin/users' }
        ];
      default:
        return [];
    }
  }, [isAuthenticated, user?.role]);

  const handleLogout = useCallback(() => {
    logout();
    navigate('/login');
  }, [logout, navigate]);

  const toggleMobileMenu = useCallback(() => {
    setMobileMenuOpen(prev => !prev);
  }, []);

  const closeMobileMenu = useCallback(() => {
    setMobileMenuOpen(false);
  }, []);

  // Memoize dashboard path
  const dashboardPath = useMemo(() => {
    return isAuthenticated ? `/${user?.role}/dashboard` : '/';
  }, [isAuthenticated, user?.role]);

  // Memoize messages path
  const messagesPath = useMemo(() => {
    return `/${user?.role}/messages`;
  }, [user?.role]);

  return (
    <header className="bg-white shadow-sm sticky top-0 z-40">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          <div className="flex items-center">
            {/* Logo */}
            <Link to={dashboardPath} className="flex items-center">
              <img
                src="/logo.svg"
                alt="GUARDLY"
                className="h-10 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            {isAuthenticated && (
              <div className="hidden md:ml-10 md:flex md:space-x-8">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium text-gray-700 hover:text-primary-600 border-b-2 border-transparent hover:border-primary-600 transition-colors"
                  >
                    {link.name}
                  </Link>
                ))}
                {/* Elite Member Link */}
                {user?.role === 'customer' && (
                  <Link
                    to="/elite"
                    className="inline-flex items-center px-1 pt-1 text-sm font-medium bg-gradient-to-r from-yellow-600 to-yellow-500 bg-clip-text text-transparent hover:from-yellow-500 hover:to-yellow-400 border-b-2 border-transparent hover:border-yellow-500 transition-all"
                  >
                    <SparklesIcon className="h-4 w-4 mr-1 text-yellow-500" />
                    Elite
                  </Link>
                )}
              </div>
            )}
          </div>

          {/* Right side items */}
          <div className="flex items-center space-x-4">
            {isAuthenticated ? (
              <>
                {/* Messages */}
                <Link
                  to={messagesPath}
                  className="relative p-2 text-gray-600 hover:text-gray-900"
                >
                  <ChatIcon className="h-6 w-6" />
                  {unreadMessageCount > 0 && (
                    <span className="absolute -top-1 -right-1 h-5 w-5 bg-orange-500 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {unreadMessageCount}
                    </span>
                  )}
                </Link>

                {/* Notifications */}
                <NotificationCenter />

                {/* User Menu */}
                <div className="relative">
                  <button className="flex items-center space-x-3 text-sm">
                    <div className="text-right hidden sm:block">
                      <p className="font-medium text-gray-900">{user?.fullName}</p>
                      <p className="text-xs text-gray-500 capitalize">{user?.role}</p>
                    </div>
                    <UserAvatar user={user} size="small" />
                  </button>
                </div>

                {/* Logout Button - Desktop */}
                <button
                  onClick={handleLogout}
                  className="hidden md:flex items-center space-x-1 text-sm text-gray-600 hover:text-gray-900"
                >
                  <LogoutIcon className="h-5 w-5" />
                  <span>Logout</span>
                </button>

                {/* Mobile menu button */}
                <button
                  onClick={toggleMobileMenu}
                  className="md:hidden p-2 text-gray-600 hover:text-gray-900"
                  aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
                  aria-expanded={mobileMenuOpen}
                >
                  {mobileMenuOpen ? (
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  ) : (
                    <MenuIcon className="h-6 w-6" aria-hidden="true" />
                  )}
                </button>
              </>
            ) : (
              <div className="flex items-center space-x-4">
                <Link
                  to="/apply"
                  className="text-sm font-medium text-gray-700 hover:text-primary-600"
                >
                  Become a Security Service Provider
                </Link>
                <Link
                  to="/waitlist"
                  className="btn btn-primary btn-sm"
                >
                  Join Waitlist
                </Link>
                {ENABLE_FULL_APP && (
                  <>
                    <Link
                      to="/login"
                      className="text-sm font-medium text-gray-700 hover:text-primary-600"
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className="btn btn-primary btn-sm"
                    >
                      Register
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Mobile Navigation */}
        <AnimatePresence>
          {mobileMenuOpen && isAuthenticated && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="md:hidden border-t border-gray-200"
            >
              <div className="py-2 space-y-1">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                    onClick={closeMobileMenu}
                  >
                    {link.name}
                  </Link>
                ))}
                <button
                  onClick={handleLogout}
                  className="block w-full text-left px-4 py-2 text-base font-medium text-gray-700 hover:bg-gray-50 hover:text-primary-600"
                >
                  Logout
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default memo(Header);
